// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { Container, Header, Button, Message, Table, Icon, Segment } from 'semantic-ui-react';
import { brokerageApi } from '../api/brokerageApi';
import { brandingApi, type BrandingInitRequest, type MigrationReport } from '../api/brandingApi';

function BrandingMigrationPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<MigrationReport | null>(null);

  const handleMigrate = async () => {
    setLoading(true);
    setError(null);
    setReport(null);

    try {
      // Brokerage branding (tier, logo, JSONB config) now lives in
      // branding-service; the resource-server BrokerageDTO no longer exposes
      // those fields. This page simply seeds a default-tier record for every
      // brokerage that doesn't already have branding in the service, leaving
      // the Admin UI to customize it afterwards.
      const page = await brokerageApi.getAll({ page: 0, size: 1000 });
      const records: BrandingInitRequest[] = page.content.map(brokerage => ({
        tenantId: brokerage.id!,
        subdomain: brokerage.subdomain,
        displayName: brokerage.name,
        brandingTier: 'NONE',
      }));

      const result = await brandingApi.migrateBranding(records);
      setReport(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Migration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header as="h2">
        <Icon name="paint brush" />
        <Header.Content>
          Branding Migration
          <Header.Subheader>
            Sync brokerage branding data to the Branding Service
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <p>
          This tool migrates branding configuration (tier, logo, colors, typography) from all
          brokerages to the dedicated Branding Service. Existing branding records will be skipped.
        </p>
        <Button
          primary
          loading={loading}
          disabled={loading}
          onClick={handleMigrate}
        >
          <Icon name="sync" />
          Run Migration
        </Button>
      </Segment>

      {error && (
        <Message negative>
          <Message.Header>Migration Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {report && (
        <Segment>
          <Header as="h3">Migration Report</Header>
          <Table definition compact>
            <Table.Body>
              <Table.Row>
                <Table.Cell width={4}>Total Brokerages</Table.Cell>
                <Table.Cell>{report.total}</Table.Cell>
              </Table.Row>
              <Table.Row positive={report.migrated > 0}>
                <Table.Cell>Migrated</Table.Cell>
                <Table.Cell>{report.migrated}</Table.Cell>
              </Table.Row>
              <Table.Row negative={report.failed > 0}>
                <Table.Cell>Failed</Table.Cell>
                <Table.Cell>{report.failed}</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>

          {report.errors.length > 0 && (
            <Message warning>
              <Message.Header>Errors</Message.Header>
              <Message.List>
                {report.errors.map((err, idx) => (
                  <Message.Item key={idx}>{err}</Message.Item>
                ))}
              </Message.List>
            </Message>
          )}

          {report.failed === 0 && report.migrated > 0 && (
            <Message positive>
              <Message.Header>Migration Complete</Message.Header>
              <p>All {report.migrated} brokerages migrated successfully.</p>
            </Message>
          )}
        </Segment>
      )}
    </Container>
  );
}

export default BrandingMigrationPage;
